import { useEffect, useMemo, useState } from 'react'
import { memberLabel } from './viewModel'

export type MemberOption = { id: string; label: string }

const STATEMENT_TYPES: MemberOption[] = [
  { id: 'profit_and_loss', label: 'P&L' },
  { id: 'balance_sheet', label: 'Balance sheet' },
  { id: 'cash_flow', label: 'Cash flow' },
]

/**
 * Searchable member picker. Members come from the saved view's data product
 * (GET /api/views/{id}/members) or, before the view is saved, from the rows
 * the preview returned. Statement types are a fixed set.
 */
export function MemberPicker({
  dimensionId,
  selected,
  onChange,
  search,
  previewMembers,
}: {
  dimensionId: string
  selected: string[]
  onChange: (next: string[]) => void
  search?: (query: string) => Promise<MemberOption[]>
  previewMembers: string[]
}) {
  const [query, setQuery] = useState('')
  const [remote, setRemote] = useState<MemberOption[] | null>(null)
  const fixed = dimensionId.endsWith('.statement_type') ? STATEMENT_TYPES : null

  useEffect(() => {
    if (fixed || !search) return
    let live = true
    const timer = setTimeout(() => {
      search(query).then((members) => live && setRemote(members)).catch(() => live && setRemote(null))
    }, 200)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [dimensionId, fixed, query, search])

  const options = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const base = fixed || remote || previewMembers.map((id) => ({ id, label: id }))
    return base.filter((option) => !needle || option.label.toLowerCase().includes(needle) || option.id.toLowerCase().includes(needle)).slice(0, 50)
  }, [fixed, previewMembers, query, remote])

  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id])

  return (
    <div className="fg-views-members">
      <div className="fg-views-chips" aria-label="Selected members">
        {selected.map((id) => (
          <button type="button" key={id} className="fg-views-chip" onClick={() => toggle(id)} aria-label={`Remove ${memberLabel(id)}`}>
            {memberLabel(id)} ×
          </button>
        ))}
      </div>
      {!fixed && (
        <input
          type="search"
          value={query}
          placeholder={search || previewMembers.length ? 'Search members…' : 'Preview the view to list its members'}
          aria-label="Search members"
          onChange={(event) => setQuery(event.target.value)}
        />
      )}
      <ul className="fg-views-member-list" role="listbox" aria-multiselectable="true">
        {options.map((option) => (
          <li key={option.id} role="option" aria-selected={selected.includes(option.id)}>
            <label>
              <input type="checkbox" checked={selected.includes(option.id)} onChange={() => toggle(option.id)} />
              {option.label}
            </label>
          </li>
        ))}
        {!options.length && <li className="fg-views-muted">No members match.</li>}
      </ul>
    </div>
  )
}
